import clsx from 'clsx';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import {
  BASE_TOOL_GROUPS,
  baseToolFieldId,
  type BaseToolDraft,
  type BaseToolField,
  type FieldIssue,
  type NumberField,
} from '../lib/baseToolSchema';
import { toolFieldLabelKey } from '../lib/toolFieldLabels';

interface BaseToolFieldsProps {
  draft: BaseToolDraft;
  issues: Record<string, FieldIssue>;
  onChange: (key: string, value: string) => void;
}

export function BaseToolFields({ draft, issues, onChange }: BaseToolFieldsProps) {
  const { t } = useTranslation();

  const label = (field: BaseToolField) => {
    const key = toolFieldLabelKey(field.key);
    return key ? t(key) : field.key;
  };

  const range = (field: NumberField) =>
    t(
      field.exclusiveMin
        ? 'toolRecommender.baseTool.rangeExclusive'
        : 'toolRecommender.baseTool.range',
      {
        min: field.min,
        max: field.max,
        unit: field.unit,
      }
    );

  // A field that is merely empty is not shouted at: the counters say what is left to do.
  const message = (field: BaseToolField): string | null => {
    const issue = issues[field.key];
    if (issue === 'outOfRange' && field.kind === 'number') {
      return t('toolRecommender.baseTool.issues.outOfRange', { range: range(field) });
    }
    if (issue === 'notANumber' || issue === 'unknownChoice') {
      return t(`toolRecommender.baseTool.issues.${issue}`);
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-6">
      {BASE_TOOL_GROUPS.map((group) => {
        const done = group.fields.filter((field) => !issues[field.key]).length;
        return (
          <fieldset
            key={group.id}
            className="flex min-w-0 flex-col gap-3"
            data-testid={`base-tool-group-${group.id}`}
          >
            <legend className="mb-3 flex w-full items-baseline gap-2">
              <span className="eyebrow">{t(`toolRecommender.baseTool.groups.${group.id}`)}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {done} / {group.fields.length}
              </span>
            </legend>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {group.fields.map((field) => {
                const id = baseToolFieldId(field.key);
                const error = message(field);
                const value = draft[field.key] ?? '';
                return (
                  <div key={field.key} className="flex min-w-0 flex-col gap-1">
                    <Label htmlFor={id} className="text-xs">
                      {label(field)}
                    </Label>
                    {field.kind === 'choice' ? (
                      <Select value={value} onValueChange={(next) => onChange(field.key, next)}>
                        <SelectTrigger id={id} className="w-full" aria-invalid={error !== null}>
                          <SelectValue placeholder={t('toolRecommender.baseTool.choose')} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="relative">
                        <Input
                          id={id}
                          inputMode="decimal"
                          className="pr-10 font-mono tabular-nums"
                          value={value}
                          onChange={(event) => onChange(field.key, event.target.value)}
                          aria-invalid={error !== null}
                          aria-describedby={`${id}-hint`}
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {field.unit}
                        </span>
                      </div>
                    )}
                    <p
                      id={`${id}-hint`}
                      className={clsx(
                        'min-h-4 text-2xs tabular-nums',
                        error ? 'text-destructive' : 'text-muted-foreground'
                      )}
                    >
                      {error ?? (field.kind === 'number' ? range(field) : '')}
                    </p>
                  </div>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
